/**
 * Customer Reviews (Klaviyo)
 *
 * Renders the Klaviyo Reviews list into the same markup the static
 * oddit-section-customer-reviews mockup uses, so the two are visually identical.
 *
 * ---------------------------------------------------------------------------
 * Which Klaviyo API this uses, and why
 * ---------------------------------------------------------------------------
 * Klaviyo has two separate reviews APIs, and on this account only one answers:
 *
 *   /client/reviews                the documented, revision-versioned public
 *                                  API. Returns 400 for this company with
 *                                  "Error fetching reviews
 *                                  error=client_api_usage_disabled" — an
 *                                  account-level feature gate only Klaviyo
 *                                  support can lift. Everything else about the
 *                                  request is valid: the company id resolves,
 *                                  CORS allows *, GET is permitted.
 *
 *   /reviews/api/client_reviews/   the endpoint Klaviyo's own onsite review
 *                                  widget calls. No revision header, no auth
 *                                  beyond company_id. Works.
 *
 * This file uses the second. That is a deliberate trade: it is undocumented and
 * unversioned, so Klaviyo could change it without notice and there is no
 * revision to pin against. It is also what their own widget runs on, so it will
 * not move unless that widget moves. If /client/reviews is ever enabled for
 * this account, switching back means changing fetchPage() and mapReview() and
 * nothing else.
 *
 * The literal 'all' in the path is the product id — it is what the widget's own
 * mount point carries (<div id="klaviyo-reviews-all" data-id="all">) and it is
 * what makes the response store-wide rather than per product.
 *
 * ---------------------------------------------------------------------------
 * Why everything is loaded up front
 * ---------------------------------------------------------------------------
 * The endpoint caps a page at 50 regardless of what `limit` asks for, and its
 * `sort` parameter rejects every value tried against it, so ordering cannot be
 * delegated to it. Rather than fight that, the whole set is pulled once (two
 * requests for ~93 reviews) and search, sort, filtering and paging all run
 * locally. Searching is then instant instead of a request per keystroke, and
 * sorting works at all. The page cap bounds it for a store that grows large.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-klaviyo-reviews';
  var ENDPOINT = 'https://fast.a.klaviyo.com/reviews/api/client_reviews/all/';

  /* Review photos are served through Klaviyo's image resizer; image_uuid is the
     tail of the S3 key and already carries its own ?updated_at cache buster. */
  var MEDIA_BASE =
    'https://reviews-media.services.klaviyo.com/abc/width:640/plain/' +
    'https://klaviyo.s3.amazonaws.com/reviews/images/';

  /* The server's real ceiling, found by asking for more and being given 50.
     Sending a larger number is accepted but misleading, so this matches it. */
  var SERVER_PAGE = 50;

  var STAR_PATH =
    'M12 0L14.8332 8.7196H24.0015L16.5842 14.1086L19.4173 22.8282L12 17.4392' +
    'L4.58266 22.8282L7.41583 14.1086L-0.00150191 8.7196H9.16683L12 0Z';

  var sectionState = new Map();

  /* ---------------------------------------------------------------- helpers */

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatCount(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* The mockup's date reads 07/03/26. */
  function formatDate(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    function pad(n) {
      return (n < 10 ? '0' : '') + n;
    }
    return (
      pad(d.getMonth() + 1) + '/' + pad(d.getDate()) + '/' + String(d.getFullYear()).slice(-2)
    );
  }

  function starsMarkup(filled, total) {
    var out = '';
    for (var i = 1; i <= total; i++) {
      out +=
        '<svg width="24" height="23" viewBox="0 0 24 23" fill="none" ' +
        'xmlns="http://www.w3.org/2000/svg" aria-hidden="true"' +
        (i > filled ? ' class="oddit-section-klaviyo-reviews__star--empty"' : '') +
        '><path d="' + STAR_PATH + '" fill="#A8F915"/></svg>';
    }
    return out;
  }

  /* Flatten the widget API's review shape into just what the card renders, so
     the markup below has one shape to think about and swapping the data source
     again touches only this function. */
  function mapReview(raw) {
    var product = raw.product || {};
    return {
      id: raw.id,
      author: raw.author || '',
      title: raw.title || '',
      content: raw.content || '',
      rating: typeof raw.rating === 'number' ? Math.round(raw.rating) : 0,
      created: raw.created_at || '',
      time: Date.parse(raw.created_at || '') || 0,
      verified: !!raw.verified,
      status: String(raw.status || '').toLowerCase(),
      type: String(raw.type || 'review').toLowerCase(),
      isStore: !!raw.is_store,
      productName: product.name || '',
      productImage: product.image_url || '',
      reviewImage: raw.image_uuid ? MEDIA_BASE + raw.image_uuid : '',
      /* Precomputed once, so filtering does not lowercase every field again on
         every keystroke. */
      haystack: (
        (raw.title || '') + ' ' + (raw.content || '') + ' ' +
        (raw.author || '') + ' ' + (product.name || '')
      ).toLowerCase()
    };
  }

  /* ------------------------------------------------------------ the section */

  function initSection(section) {
    if (!section) return;
    destroySection(section);

    var companyId = (section.dataset.klaviyoCompanyId || '').trim();

    var els = {
      list: section.querySelector('[data-reviews-list]'),
      status: section.querySelector('[data-reviews-status]'),
      count: section.querySelector('[data-reviews-count]'),
      loadMore: section.querySelector('[data-load-more]'),
      search: section.querySelector('[data-reviews-search]'),
      pills: Array.prototype.slice.call(section.querySelectorAll('[data-filter-term]')),
      sortWrap: section.querySelector('[data-sort-wrap]'),
      sortBtn: section.querySelector('[data-reviews-sort]'),
      sortLabel: section.querySelector('[data-sort-label]'),
      sortMenu: section.querySelector('[data-sort-menu]'),
      rating: section.querySelector('[data-reviews-rating]'),
      ratingNumber: section.querySelector('[data-rating-number]'),
      ratingStars: section.querySelector('[data-rating-stars]'),
      ratingText: section.querySelector('[data-rating-text]')
    };
    if (!els.list) return;

    var opts = {
      companyId: companyId,
      status: section.dataset.klaviyoStatus || 'published',
      perPage: parseInt(section.dataset.reviewsPerPage, 10) || 5,
      pageCap: parseInt(section.dataset.pageCap, 10) || 10,
      hideQuestions: section.dataset.hideQuestions !== 'false',
      showProduct: section.dataset.showProduct !== 'false',
      loadingText: els.status ? els.status.textContent.trim() : '',
      emptyText: section.dataset.emptyText || '',
      errorText: section.dataset.errorText || ''
    };

    var state = {
      abort: typeof AbortController !== 'undefined' ? new AbortController() : null,
      all: [],
      view: [],
      shown: 0,
      sort: 'newest',
      search: '',
      terms: [],
      summary: null,
      searchTimer: null
    };
    sectionState.set(section, state);

    var signal = state.abort ? { signal: state.abort.signal } : undefined;

    function on(el, type, fn, extra) {
      if (!el) return;
      el.addEventListener(type, fn, Object.assign({}, extra || {}, signal));
    }

    /* ------------------------------------------------------------- requests */

    function fetchPage(offset) {
      var url =
        ENDPOINT +
        '?company_id=' + encodeURIComponent(opts.companyId) +
        '&limit=' + SERVER_PAGE +
        '&offset=' + offset;

      return fetch(url, {
        signal: state.abort ? state.abort.signal : undefined
      }).then(function (res) {
        if (!res.ok) throw new Error('Klaviyo responded ' + res.status);
        return res.json();
      });
    }

    /* Walk the pages until the server says there are no more, or the cap is
       reached. The cap is there so a store with thousands of reviews cannot
       turn one page load into fifty requests; the count line still reports the
       true total, which comes from the payload rather than from what loaded. */
    function loadAll() {
      var collected = [];

      function step(offset, page) {
        return fetchPage(offset).then(function (body) {
          if (state.summary === null && body.summary) state.summary = body.summary;

          var rows = body.reviews || [];
          for (var i = 0; i < rows.length; i++) collected.push(mapReview(rows[i]));

          if (body.has_more && rows.length && page + 1 < opts.pageCap) {
            return step(offset + rows.length, page + 1);
          }
          return collected;
        });
      }

      return step(0, 0);
    }

    /* --------------------------------------------------------------- render */

    function setStatus(message) {
      if (!els.status) return;
      els.status.textContent = message || '';
      els.status.hidden = !message;
    }

    function reviewCard(r) {
      /* The mockup's reviewer slot held one image. A photo the reviewer
         actually submitted belongs there ahead of the product shot. */
      var media = '';
      if (r.reviewImage) {
        media =
          '<img class="oddit-section-customer-reviews__reviewer-img" src="' +
          esc(r.reviewImage) + '" alt="" loading="lazy">';
      } else if (opts.showProduct && r.productImage) {
        media =
          '<img class="oddit-section-customer-reviews__reviewer-img" src="' +
          esc(r.productImage) + '" alt="' + esc(r.productName) +
          '" loading="lazy" width="119" height="44">';
      }

      return (
        '<article class="oddit-section-customer-reviews__review-card" data-review-card>' +
          '<div class="oddit-section-customer-reviews__review-inner">' +
            '<span class="oddit-section-customer-reviews__review-date">' +
              esc(formatDate(r.created)) +
            '</span>' +
            '<div class="oddit-section-customer-reviews__review-content">' +
              '<div class="oddit-section-customer-reviews__review-header">' +
                (r.rating
                  ? '<div class="oddit-section-customer-reviews__review-stars" role="img" ' +
                    'aria-label="' + r.rating + ' out of 5 stars">' +
                    starsMarkup(r.rating, 5) + '</div>'
                  : '') +
                (r.title
                  ? '<h3 class="oddit-section-customer-reviews__review-title">' +
                    esc(r.title) + '</h3>'
                  : '') +
              '</div>' +
              (r.content
                ? '<p class="oddit-section-customer-reviews__review-body">' +
                  esc(r.content) + '</p>'
                : '') +
            '</div>' +
            '<div class="oddit-section-customer-reviews__review-reviewer">' +
              media +
              (r.author
                ? '<span class="oddit-section-klaviyo-reviews__reviewer-name">' +
                  esc(r.author) + '</span>'
                : '') +
              (r.verified
                ? '<span class="oddit-section-klaviyo-reviews__verified">Verified Buyer</span>'
                : '') +
            '</div>' +
          '</div>' +
        '</article>'
      );
    }

    /* The mockup shipped "4.8" and "based on 1,234 reviews" as literal text.
       Both now come from the payload's own summary, so nothing is estimated —
       and the block stays hidden if the store has no rating yet. */
    function renderSummary() {
      if (!els.rating) return;
      var s = state.summary || {};
      var avg = typeof s.star_rating === 'number' ? s.star_rating : 0;
      var total = typeof s.review_count === 'number' ? s.review_count : 0;
      if (!avg || !total) {
        els.rating.hidden = true;
        return;
      }
      els.rating.hidden = false;
      if (els.ratingNumber) els.ratingNumber.textContent = avg.toFixed(1);
      if (els.ratingStars) {
        els.ratingStars.innerHTML = starsMarkup(Math.round(avg), 5);
        els.ratingStars.setAttribute('aria-label', avg.toFixed(1) + ' out of 5 stars');
      }
      if (els.ratingText) {
        els.ratingText.textContent = 'based on ' + formatCount(total) + ' reviews';
      }
    }

    function render() {
      els.list.innerHTML = state.view.slice(0, state.shown).map(reviewCard).join('');
      els.list.setAttribute('aria-busy', 'false');

      setStatus(state.view.length ? '' : opts.emptyText);

      if (els.count) {
        /* The filtered length while a search or a pill is narrowing things, the
           store's own total otherwise — the mockup's "1,234 Reviews". */
        var narrowed = state.search || state.terms.length;
        var n = narrowed
          ? state.view.length
          : (state.summary && state.summary.review_count) || state.view.length;
        els.count.textContent = n ? formatCount(n) + ' Reviews' : '';
      }

      if (els.loadMore) els.loadMore.hidden = state.shown >= state.view.length;
    }

    /* -------------------------------------------------------------- queries */

    var SORTS = {
      newest: function (a, b) { return b.time - a.time; },
      oldest: function (a, b) { return a.time - b.time; },
      highest: function (a, b) { return b.rating - a.rating || b.time - a.time; },
      lowest: function (a, b) { return a.rating - b.rating || b.time - a.time; }
    };

    function applyQuery() {
      var q = state.search.toLowerCase();

      state.view = state.all.filter(function (r) {
        if (opts.hideQuestions && r.type === 'question') return false;
        if (opts.status !== 'all' && r.status && r.status !== opts.status) return false;
        if (q && r.haystack.indexOf(q) === -1) return false;
        /* Several pills narrow together, as the ANDed server-side filters in
           the previous build did. */
        for (var i = 0; i < state.terms.length; i++) {
          if (r.haystack.indexOf(state.terms[i].toLowerCase()) === -1) return false;
        }
        return true;
      });

      state.view.sort(SORTS[state.sort] || SORTS.newest);
      state.shown = Math.min(opts.perPage, state.view.length);
      render();
    }

    /* ------------------------------------------------------------- controls */

    on(els.search, 'input', function () {
      if (state.searchTimer) clearTimeout(state.searchTimer);
      /* Everything is local now, so this only smooths rendering rather than
         throttling a request per keystroke. */
      state.searchTimer = setTimeout(function () {
        state.searchTimer = null;
        var value = els.search.value.trim();
        if (value === state.search) return;
        state.search = value;
        applyQuery();
      }, 120);
    });

    els.pills.forEach(function (pill) {
      on(pill, 'click', function () {
        var active = pill.classList.toggle('is-active');
        pill.setAttribute('aria-pressed', active ? 'true' : 'false');
        state.terms = els.pills
          .filter(function (p) { return p.classList.contains('is-active'); })
          .map(function (p) { return p.dataset.filterTerm || ''; })
          .filter(Boolean);
        applyQuery();
      });
    });

    function closeSort() {
      if (!els.sortMenu || !els.sortBtn) return;
      els.sortMenu.hidden = true;
      els.sortBtn.setAttribute('aria-expanded', 'false');
      els.sortBtn.classList.remove('is-open');
    }

    on(els.sortBtn, 'click', function (e) {
      e.stopPropagation();
      if (!els.sortMenu) return;
      var open = els.sortMenu.hidden;
      els.sortMenu.hidden = !open;
      els.sortBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      els.sortBtn.classList.toggle('is-open', open);
    });

    if (els.sortMenu) {
      Array.prototype.forEach.call(
        els.sortMenu.querySelectorAll('[data-sort-value]'),
        function (option) {
          on(option, 'click', function () {
            Array.prototype.forEach.call(
              els.sortMenu.querySelectorAll('[data-sort-value]'),
              function (other) {
                var selected = other === option;
                other.classList.toggle('is-selected', selected);
                if (other.parentNode) {
                  other.parentNode.setAttribute('aria-selected', selected ? 'true' : 'false');
                }
              }
            );
            if (els.sortLabel) els.sortLabel.textContent = option.textContent;
            closeSort();

            var value = option.dataset.sortValue;
            if (value === state.sort) return;
            state.sort = value;
            applyQuery();
          });
        }
      );

      on(document, 'click', function (e) {
        if (els.sortWrap && !els.sortWrap.contains(e.target)) closeSort();
      });
      on(document, 'keydown', function (e) {
        if (e.key === 'Escape') closeSort();
      });
    }

    on(els.loadMore, 'click', function () {
      state.shown = Math.min(state.shown + opts.perPage, state.view.length);
      render();
    });

    /* ----------------------------------------------------------------- boot */

    if (!opts.companyId) {
      els.list.setAttribute('aria-busy', 'false');
      if (els.loadMore) els.loadMore.hidden = true;
      return;
    }

    els.list.setAttribute('aria-busy', 'true');
    setStatus(opts.loadingText);

    loadAll()
      .then(function (reviews) {
        state.all = reviews;
        renderSummary();
        applyQuery();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        els.list.setAttribute('aria-busy', 'false');
        els.list.innerHTML = '';

        /* Klaviyo answers with a real reason. Swallowing every one behind a
           single friendly line is what made the last outage take a terminal to
           diagnose, so the reason goes to the console, and in the theme editor
           it goes on screen: the only person looking there is the one who can
           act on it. */
        if (window.console && console.warn) {
          console.warn('[Klaviyo reviews] ' + (err && err.message ? err.message : err));
        }
        var inEditor = !!(window.Shopify && window.Shopify.designMode);
        setStatus(
          inEditor && err && err.message
            ? opts.errorText + ' (' + err.message + ')'
            : opts.errorText
        );

        if (els.loadMore) els.loadMore.hidden = true;
      });
  }

  function destroySection(section) {
    var state = sectionState.get(section);
    if (!state) return;
    if (state.searchTimer) clearTimeout(state.searchTimer);
    if (state.abort) state.abort.abort();
    sectionState.delete(section);
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll(SECTION_SELECTOR), initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (!section && e.target.matches && e.target.matches(SECTION_SELECTOR)) section = e.target;
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();
